import { Link, router } from '@inertiajs/react'
import { MessageCircle, ThumbsUp, CheckCircle, HelpCircle, MessageSquare, Megaphone, BadgeCheck } from 'lucide-react'
import type { CommunityPost } from '@/types'

interface PostCardProps {
  post: CommunityPost
}

const POST_TYPE_CONFIG = {
  question: {
    icon: HelpCircle,
    label: 'Question',
    className: 'bg-blue-100 text-blue-800'
  },
  discussion: {
    icon: MessageSquare,
    label: 'Discussion',
    className: 'bg-gray-100 text-gray-800'
  },
  announcement: {
    icon: Megaphone,
    label: 'Annonce',
    className: 'bg-tessan-cream text-tessan-primary'
  }
}

function formatRelativeDate(dateString: string): string {
  const date = new Date(dateString)
  const now = new Date()
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000)

  if (diffInSeconds < 60) return "à l'instant"
  if (diffInSeconds < 3600) return `il y a ${Math.floor(diffInSeconds / 60)} min`
  if (diffInSeconds < 86400) return `il y a ${Math.floor(diffInSeconds / 3600)} h`
  if (diffInSeconds < 604800) return `il y a ${Math.floor(diffInSeconds / 86400)} j`

  return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })
}

function getInitials(name: string): string {
  return name
    .split(' ')
    .map(part => part[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)
}

export default function PostCard({ post }: PostCardProps) {
  const typeConfig = POST_TYPE_CONFIG[post.post_type]
  const TypeIcon = typeConfig.icon
  const isOfficial = Boolean(post.is_official)

  const handleCardClick = () => {
    router.visit(`/posts/${post.id}`)
  }

  return (
    <article
      onClick={handleCardClick}
      className={`rounded-lg border p-4 hover:shadow-md transition-all cursor-pointer ${
        isOfficial
          ? 'bg-tessan-cream/40 border-tessan-primary/40 hover:border-tessan-primary'
          : 'bg-white border-gray-200 hover:border-tessan-primary/30'
      }`}
    >
      <div className="flex items-start gap-3">
        {/* Avatar */}
        <div className="flex-shrink-0 w-10 h-10 rounded-full bg-tessan-primary text-white flex items-center justify-center text-sm font-medium">
          {isOfficial ? <BadgeCheck className="w-5 h-5" /> : getInitials(post.author.name)}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* Header */}
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className="font-medium text-gray-900">
              {isOfficial ? 'Tessan' : post.author.name}
            </span>
            {!isOfficial && (
              <>
                <span className="text-gray-400">·</span>
                <span className="text-sm text-gray-500">{post.author.specialty}</span>
              </>
            )}
            <span className="text-gray-400">·</span>
            <time className="text-sm text-gray-500" dateTime={post.created_at}>
              {formatRelativeDate(post.created_at)}
            </time>
          </div>

          {/* Badges */}
          <div className="flex items-center gap-2 flex-wrap mb-2">
            {/* Badge officiel Tessan (contenu publié par un admin) */}
            {isOfficial && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-tessan-primary text-white">
                <BadgeCheck className="w-3 h-3" />
                Contenu Tessan
              </span>
            )}
            {/* Badge profession (uniquement dans le fil partagé Médecins/Pharmaciens, hors contenu officiel) */}
            {!isOfficial && post.community && (
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                post.community.slug === 'pharmaciens'
                  ? 'bg-teal-100 text-teal-800'
                  : 'bg-indigo-100 text-indigo-800'
              }`}>
                {post.community.name}
              </span>
            )}
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${typeConfig.className}`}>
              <TypeIcon className="w-3 h-3" />
              {typeConfig.label}
            </span>
            {post.category && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                {post.category.name}
              </span>
            )}
            {post.has_solution && post.post_type === 'question' && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                <CheckCircle className="w-3 h-3" />
                Résolu
              </span>
            )}
          </div>

          {/* Title */}
          <Link
            href={`/posts/${post.id}`}
            onClick={(e) => e.stopPropagation()}
            className="block text-lg font-semibold text-gray-900 hover:text-tessan-primary mb-2"
          >
            {post.title}
          </Link>

          {/* Excerpt */}
          <p className="text-gray-600 text-sm mb-3 line-clamp-2">
            {post.excerpt}
          </p>

          {/* Footer */}
          <div className="flex items-center gap-4 text-sm text-gray-500">
            <span className="inline-flex items-center gap-1">
              <ThumbsUp className="w-4 h-4" />
              {post.likes_count}
            </span>
            <span className="inline-flex items-center gap-1">
              <MessageCircle className="w-4 h-4" />
              {post.comments_count}
            </span>
          </div>
        </div>
      </div>
    </article>
  )
}
